import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  makeStyles,
  Paper,
  TextField,
  Typography,
  useMediaQuery
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toastr } from 'react-redux-toastr';
import ProductHeader from '../Header';
import AppButton from '../../../components/AppButton';
import SectionHeading from 'components/SectionHeading';
import { AmountType } from '../../../Cart/interfaces/item';
import formatPriceCent from '../../../utils/format-price-cent';
import { sendEvent } from '../../../utils/google-analytics';
import { removePayPalGuard } from '../../../utils/remove-paypal-guard';
import { Product, Repair } from '../../../types';
import RunningLine from 'components/RunningLine';
import cookies from 'js-cookie';
import { useDispatch } from 'react-redux';
import { setCreationTime, setVoucherCart } from 'Cart/store/actions';
import i18n from 'i18n';
import { uploadImagesFiles } from 'Estimation/store/actions';
import AddAPhotoIcon from '@material-ui/icons/AddAPhoto';
import CloseIcon from '@material-ui/icons/Close';
import { Close } from '@material-ui/icons';
import { ReactComponent as Information } from '../../../images/svg/information.svg';
import Gallery from './Gallery';
import Testimonials from '../../../Homepage/components/Testimonials';
import AgeSlider from '../../../components/AgeSlider/AgeSlider';
import useRepairUpload, { RepairUpload } from './useRepairUpload';
import { ACCEPTED_IMAGES_STRING, MAX_UPLOAD_IMAGES } from '../../../utils/upload-constants';
import { config } from '../../../config';
import Steps from '../Steps/Steps';
import articleNumberImage from '../CategoryStep/article_number.jpeg';

// env
const cartEnabled: boolean = process.env.REACT_APP_CART_ENABLED === 'true';
/*const cartEnabled = false;  // HARDCODE*/
// props; exporting to tests
export interface ProductDetailsStepFormProps {
  product: Product;
  onSubmit(
    amount?: number,
    comment?: string,
    creationTime?: number,
    images?: string[],
    brand?: string,
    age?: number,
    artikelNumber?: string,
    damageImages?: string[]
  ): Promise<void>;
  backLinkHref?: string;
  itemId?: string;
}

export interface RepairImage {
  file: File;
  fileName: string;
  fileType: string;
  fileSize: number;
  fileContent: string;
  creationTime: number;
}

const useStyles = makeStyles((theme) => ({
  filePreviewGrid: {
    display: 'grid',
    gap: theme.spacing(1),
    margin: theme.spacing(2, 0),
    gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))',
    width: '100%',
  },
  filePreviewItem: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    // The tiles stay square whatever the photo's aspect ratio, as two to a row in the mock.
    paddingTop: '100%',
    backgroundColor: '#f9f9f9',
    overflow: 'hidden',
    '& > *:not(button)': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
    },
  },
  filePreview: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  filePreviewVideo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  filePreviewUploading: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: 0.6,
  },
  removeButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 20,
    height: 20,
    padding: 0,
    color: '#fff',
    backgroundColor: '#000000',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 120ms ease',
    '& svg': {
      width: 14,
      height: 14,
    },
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.75)'
    },
  },
  requiredMark: {
    color: theme.palette.secondary.main,
  },
  uploadTiles: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: '1fr 1fr',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  uploadTile: {
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(2),
    border: '1px dashed rgba(0, 0, 0, 0.35)',
  },
  cameraBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      fill: '#000000',
      width: 28,
      height: 28,
    },
  },
  tileSubtitle: {
    // Two lines of body2, so a German subtitle that wraps cannot push this tile's link below
    // its neighbour's — and a one-line subtitle gains no extra gap beneath it.
    minHeight: '2.5em',
  },
  attachLink: {
    display: 'inline-block',
    color: theme.palette.secondary.main,
    fontSize: '0.75rem',
    fontWeight: 800,
    // Tracking removed too: the German label is long and this is what fits it in the tile.
    letterSpacing: 'normal',
    maxWidth: '100%',
    textTransform: 'uppercase',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  attachLinkDisabled: {
    opacity: 0.5,
    cursor: 'default',
  },
  moreLink: {
    color: `${theme.palette.secondary.main} !important`,
    fontWeight: 800,
    textDecoration: 'underline',
    textTransform: 'uppercase',
    cursor: 'pointer',
  },
  dialog: {
    position: 'relative',
  },
  dialogPaper: {
    backgroundColor: '#fff !important',
  },
  closeBtn: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1.4),
    padding: 0,
  },
  largeContent: {
    padding: '0',
    marginBottom: theme.spacing(2.5),
  },
  modalText: {
    lineHeight: 1.7,
    textAlign: 'center',
    '&:first-of-type': {
      marginBottom: theme.spacing(1.8),
    },
  },
  modalButton: {
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  modalImage: {
    width: '100%',
    maxWidth: 200,
    marginTop: theme.spacing(2),
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
}));

// A stored file is only a URL by the time it is shown again, so the kind is read off the
// extension rather than off the File that produced it.
const isVideoUrl = (url: string) => /\.(mp4|webm|ogg|mov|avi)(\?|#|$)/i.test(url);

/**
 * Product "Further Details" presentational form component.
 */
export default function ProductDetailsStepForm({ product, onSubmit, backLinkHref = '..', itemId }: ProductDetailsStepFormProps) {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const langCookie = cookies.get('language') || '';
  const headingHtmlId = 'ProductDetailsStepForm-heading';
  const commentFieldHtmlId = 'ProductDetailsStepForm-commentField';
  const categoryId = product.productCategory.seo?.[i18n.language];
  const typeId = product.productType.seo?.[i18n.language];
  const repairId = product.seo?.[i18n.language];
  const priceCent = product.priceCent;
  const productImages = product.images;
  const [comment, setComment] = useState('');
  const [newCreationTime] = useState(Date.now());
  const [inheritedImages, setInheritedImages] = useState<string[]>([]);
  // Two independent sections: the whole garment, and close-ups of the damage. Both are required.
  const overallUpload = useRepairUpload(MAX_UPLOAD_IMAGES, inheritedImages);
  const damageUpload = useRepairUpload();
  const isMobile = useMediaQuery(({ breakpoints }) => breakpoints.down('sm'));
  const cart = localStorage.getItem('cart') as string;

  let initialAmount = 1;
  if (product.input?.type === AmountType.LENGTH || product.input?.type === AmountType.WIDTH) {
    initialAmount = 0;
  }

  const [amount, setAmount] = useState(initialAmount);
  const [brand, setBrand] = useState<string>();
  const [artikelNumber, setArtikelNumber] = useState<string>();
  const [age, setAge] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [articleNumberInfoOpen, setArticleNumberInfoOpen] = useState(false);
  const { detailedDescription } = product;

  useEffect(() => {
    if (cart && itemId) {
      const product = JSON.parse(cart).find((item: Repair) => item.product.itemId === itemId);
      setBrand(product.brand);
      setAge(product.age);
      setArtikelNumber(product.artikelNumber);
      // The overall photos describe the garment, not the repair, so they are asked for once and
      // carried over to every further repair of it — the same way brand, age and article number are.
      setInheritedImages(product.images ?? []);
    }
  }, [cart])

  const overallImages = overallUpload.validImages;


  function handleCommentChange(e) {
    setComment(e.target.value);
  }

  function handleAmountChange(e) {
    setAmount(e.target.value);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (localStorage.getItem('estimationId')) {
      toastr.error(
        t('Offer in progress'),
        t('You cannot add new items while completing an offer. Return to your cart to continue.'),
      );
      return;
    }

    const validDamageImages = damageUpload.validImages;

    if (!overallImages.length || !validDamageImages.length) {
      return;
    }
    setLoading(true);
    const setVoucherInactive = false;

    dispatch(setVoucherCart(setVoucherInactive));
    dispatch(uploadImagesFiles());
    localStorage.setItem('voucherItem', JSON.stringify(setVoucherInactive));

    try {
      // @ts-ignore
      await onSubmit(Number(amount), comment, newCreationTime, overallImages, brand, age, artikelNumber, validDamageImages);
      dispatch(setCreationTime(newCreationTime));
    } catch (error) {
      console.error('Failed to submit product:', error);
    } finally {
      setLoading(false);
      removePayPalGuard();
      sendEvent('repair', 'add_repair_to_cart');
    }
  };

  function renderAmountField() {
    if (product.input?.type === AmountType.NO_INPUT || !product.input?.type) {
      return (
        <>
          <Box display="flex" alignItems="center" mb={4} gridGap="30px" style={{ gap: '30px' }}>
            {config.showBrandField && (
              <Box width={{ xs: '50%', sm: '50%' }}>
                <Typography component="label" gutterBottom htmlFor={'brand'}>
                  {t('Brand') + '*'}
                </Typography>
                <TextField
                  fullWidth
                  name="brand"
                  id={'brand'}
                  onChange={(value) => setBrand(value.target.value)}
                  required
                  size="small"
                  type="input"
                  value={brand}
                  variant="outlined"
                />
              </Box>
            )}
            {config.showArtikelNumberField && (
              <Box width={{ xs: '50%', sm: '50%' }}>
                <Box display="flex" alignItems="center" flexWrap="nowrap" style={{ gap: 6 }} mb={0.5}>
                  <Typography component="label" htmlFor={'artikelNumber'} style={{ marginBottom: 0 }}>
                    {t('artikelNumber')}
                  </Typography>
                  <Button className={classes.moreLink} onClick={() => setArticleNumberInfoOpen(true)} style={{ minWidth: 0, padding: 4 }}>
                    <Information />
                  </Button>
                </Box>
                <TextField
                  fullWidth
                  name="artikelNumber"
                  id={'artikelNumber'}
                  onChange={(value) => setArtikelNumber(value.target.value)}
                  size="small"
                  type="input"
                  value={artikelNumber}
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
        </>
      );
    }

    const amountFieldHtmlId = 'ProductDetailsStepForm-amountField';
    const step = product.input?.default ?? 1;
    return (
      <>
        <Box display="flex" alignItems="center" mb={4} gridGap="30px" style={{ gap: '30px' }}>
          <Box width={{ xs: '50%', sm: '50%' }}>
            <Typography component="label" gutterBottom htmlFor={amountFieldHtmlId}>
              {product.input?.text
                ? (product.input?.unit ? t(product.input?.text) + ', ' + product.input?.unit : t(product.input?.text)) + '*'
                : t('Amount') + '*'}
            </Typography>
            <TextField
              fullWidth
              id={amountFieldHtmlId}
              inputProps={{
                min: +step,
                step: +step,
                style: { textAlign: 'center' },
              }}
              name="quantity"
              onChange={handleAmountChange}
              required
              //required={quantityInput.required}
              size="small"
              type="number"
              value={amount}
              variant="outlined"
            />
          </Box>

          <>
            {config.showBrandField && (
              <Box width={{ xs: '50%', sm: '50%' }}>
                <Typography component="label" gutterBottom htmlFor={'brand'}>
                  {t('Brand') + '*'}
                </Typography>
                <TextField
                  fullWidth
                  name="brand"
                  id={'brand'}
                  onChange={(value) => setBrand(value.target.value)}
                  required
                  size="small"
                  type="input"
                  value={brand}
                  variant="outlined"
                />
              </Box>
            )}
            {config.showArtikelNumberField && (
              <Box width={{ xs: '50%', sm: '50%' }}>
                <Box display="flex" alignItems="center" flexWrap="nowrap" style={{ gap: 6 }} mb={0.5}>
                  <Typography component="label" htmlFor={'artikelNumber'} style={{ marginBottom: 0 }}>
                    {t('artikelNumber')}
                  </Typography>
                  <Button className={classes.moreLink} onClick={() => setArticleNumberInfoOpen(true)} style={{ minWidth: 0, padding: 4 }}>
                    <Information />
                  </Button>
                </Box>
                <TextField
                  fullWidth
                  name="artikelNumber"
                  id={'artikelNumber'}
                  onChange={(value) => setArtikelNumber(value.target.value)}
                  size="small"
                  type="input"
                  value={artikelNumber}
                  variant="outlined"
                />
              </Box>
            )}
          </>
        </Box>
      </>
    );
  }

  function renderImageField(upload: RepairUpload, inputId: string, title: string, subtitle: string) {
    const { images, fileTypes, uploadProgress, videoPosters, maxUploads, handleFileChange, removeImage } = upload;
    const isFull = images.length >= maxUploads;

    return (
      <label htmlFor={inputId} className={classes.uploadTile}>
        <Box className={classes.cameraBadge}>
          <AddAPhotoIcon />
        </Box>
        <Typography component="h2" variant="h6">{title}</Typography>
        <Typography variant="body2" className={classes.tileSubtitle}>{subtitle}</Typography>
        {images.length > 0 && (
          <Box className={classes.filePreviewGrid}>
            {images.map((imageUrl, index) => {
              const isUploading = uploadProgress[index] !== undefined && (uploadProgress[index] < 100 || !imageUrl);
              const isFailed = typeof imageUrl === 'string' && imageUrl === 'failed-upload';
              if (isFailed) return null;
              return (
                <Box key={index} className={classes.filePreviewItem}>
                  {isUploading ? (
                    <Box className={classes.filePreviewUploading} display="flex" alignItems="center" justifyContent="center" bgcolor="#f0f0f0">
                      <Box position="relative" display="flex" alignItems="center" justifyContent="center">
                        <CircularProgress variant="determinate" value={100} size={40} thickness={5} style={{ color: 'rgba(91,188,204,0.15)', position: 'absolute' }} />
                        <CircularProgress
                          variant={uploadProgress[index] !== undefined ? 'determinate' : 'indeterminate'}
                          value={uploadProgress[index] || 0}
                          size={40}
                          thickness={5}
                          color="secondary"
                          style={{ transition: 'opacity 260ms ease', opacity: (uploadProgress[index] === 100 && imageUrl) ? 0 : 1 }}
                        />
                        {uploadProgress[index] !== undefined && (
                          <Typography variant="caption" style={{ position: 'absolute', fontWeight: 600, color: '#000', fontSize: '0.625rem' }}>
                            {uploadProgress[index]}%
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    (fileTypes[index]?.startsWith('video/') || isVideoUrl(imageUrl)) ? (
                      <video
                        src={`${imageUrl}#t=0.1`}
                        className={classes.filePreviewVideo}
                        muted
                        playsInline
                        preload="metadata"
                        poster={videoPosters[index] || undefined}
                      />
                    ) : (
                      <img src={imageUrl} alt={`Upload ${index + 1}`} className={classes.filePreview} />
                    )
                  )}
                  <IconButton className={classes.removeButton} size="small" onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeImage(index); }} aria-label="Remove file">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        )}
        <input id={inputId} type="file" accept={ACCEPTED_IMAGES_STRING} onChange={handleFileChange} style={{ display: 'none' }} multiple disabled={isFull} />
        <Typography component="span" className={`${classes.attachLink} ${isFull ? classes.attachLinkDisabled : ''}`}>
          + {t('Upload Image')}&nbsp;<span style={{ whiteSpace: 'nowrap' }}>( {images.length} / {maxUploads} )*</span>
        </Typography>
      </label>
    );
  }

  return (
    <Box>
      <Container maxWidth="xl">
        <Steps currentStep={2} />
        <Box mb={4} />

        <ProductHeader categoryId={categoryId} typeId={typeId} repairId={repairId} priceCent={priceCent} backLinkHref={backLinkHref} />
        <Box mb={3} />
        <SectionHeading id={headingHtmlId} component="h1" size="large">
          {t('Further Details')}
        </SectionHeading>
        <Box mb={3} />

        <form aria-labelledby={headingHtmlId} onSubmit={handleSubmit}>
          <Paper
            style={{
              padding: 0,
              display: 'flex',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              justifyContent: 'space-between',
              gap: '30px',
              flexDirection: isMobile ? 'column-reverse' : 'row',
            }}
          >
            <Box width={isMobile ? '100%' : !!productImages?.length ? '50%' : '100%'}>
              {renderAmountField()}

              <Box width={'100%'}>
                <Typography component="label" gutterBottom htmlFor={'age'}>
                  {t('how_many_month_ago_details')+'*'}
                </Typography>
                <AgeSlider
                  value={age}
                  disabled={!!itemId}
                  onChange={(value) => setAge(+value)}
                  id={'age'}
                />
              </Box>

              <Box mb={3} />

              <Typography component="label" gutterBottom htmlFor={commentFieldHtmlId}>
                {t('Comments')}
              </Typography>
              <TextField
                fullWidth
                id={commentFieldHtmlId}
                multiline
                name="comment"
                onChange={handleCommentChange}
                rows={3}
                size="small"
                value={comment}
                variant="outlined"
              />
              {detailedDescription && (
                <>
                  <Box mb={3} />
                  <Typography style={{whiteSpace: 'pre-line'}} variant="body1">{(!langCookie ? detailedDescription.de : detailedDescription[langCookie]).replaceAll('\\u003a', ':')}</Typography>
                  <Box mb={4} />
                </>
              )}
              <Typography component="h2" variant="h6">
                {t('product_repair_photos')} <span className={classes.requiredMark}>*</span>
              </Typography>
              <Box mb={1} />
              <Typography>{t('ImageProductUploadText')}</Typography>
              <Box mb={2} />
              <Box className={classes.uploadTiles}>
                  {renderImageField(overallUpload, 'file-upload', t('upload_overall_title'), t('upload_overall_subtitle'))}
                {renderImageField(damageUpload, 'damage-file-upload', t('upload_damage_title'), t('upload_damage_subtitle'))}
              </Box>
              <Box mb={4} />
              <Box>
                <Typography color="secondary" variant="caption">
                  {t('fields_with_asterisks')}
                </Typography>
              </Box>
              <Box mb={2} />
            </Box>
            {!!productImages?.length && (
              <Box width={isMobile ? '100%' : '46%'} style={{ paddingBottom: isMobile ? '30px' : undefined }}>
                <Gallery images={productImages} hideThumbs />
              </Box>
            )}
          </Paper>
          <Box mb={4} />
          <Box textAlign="center">
            <AppButton
              color="secondary"
              disabled={!cartEnabled || !overallImages.length || !damageUpload.validImages.length}
              loading={loading}
              type="submit"
              variant="contained"
            >
              {t('Add to Cart')}
            </AppButton>
          </Box>
        </form>
      </Container>
      <Dialog
        className={classes.dialog}
        maxWidth="sm"
        onClose={() => setArticleNumberInfoOpen(false)}
        open={articleNumberInfoOpen}
        PaperProps={{ className: classes.dialogPaper }}
      >
        <IconButton aria-label="close" className={classes.closeBtn} color="secondary" onClick={() => setArticleNumberInfoOpen(false)}>
          <Close />
        </IconButton>
        <Box textAlign="center">
          <Information className="mb-3" />
          <Typography variant="h3" className="mb-3">
            {t('Article Number Modal - Title')}
          </Typography>
        </Box>
        <DialogContent className={classes.largeContent}>
          <Typography variant="body2" className={classes.modalText}>
            {t('Article Number Modal - Information 1')}
          </Typography>
          <Typography variant="body2" className={classes.modalText}>
            {t('Article Number Modal - Information 2')}
          </Typography>
          <img
            src={articleNumberImage}
            alt=""
            className={classes.modalImage}
          />
        </DialogContent>
        <Box textAlign="center">
          <Button color="secondary" onClick={() => setArticleNumberInfoOpen(false)} variant="contained" className={classes.modalButton}>
            {t('BackToProduct')}
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
}
